import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react"
import type { IAudioPlayerRef } from "../Player/interfaces"

/**
 * Shared transport state for the single recording player on the meeting-detail
 * page. Every summary "fragment" chip (decisions, action items, open
 * questions, quotes) reads this so it can render a play/pause + rewind control
 * synced to the one `Player` — instead of the old one-shot jump-and-play.
 *
 * Mirrors the `SpeakerColorsProvider` pattern: a context with a no-op default
 * so dumb consumers render harmlessly when no provider is mounted (e.g. in
 * isolated stories), and chips simply hide (`available: false`).
 */
export interface IMeetingPlayerControls {
  /** True when a real player is mounted (the meeting has a recording). When false, chips hide. */
  available: boolean
  /** True while the shared player is playing. */
  playing: boolean
  /** The fragment offset currently loaded into the player (the chip the user last triggered), or null. */
  activeSeekTime: number | null
  /** Play this fragment: resume in place when it's already active, else seek to it and play. */
  play: (seconds: number) => void
  /** Pause playback in place. */
  pause: () => void
  /** Restart this fragment from its start (re-seek + play). */
  rewind: (seconds: number) => void
  /** Seek to `start` and play until `end` (the Who spoke sample button). */
  playWindow: (start: number, end: number) => void
  /** Resume from the current position. Omitted on older call sites. */
  resume?: () => void
  /** Playback rate the full player and the dock share. */
  speed?: number
  setSpeed?: (speed: number) => void
  /**
   * Time updates during playback. Subscribers keep their own state so a
   * tick does not re-render the whole summary.
   */
  subscribeProgress?: (listener: (currentTime: number, duration: number) => void) => () => void
}

const NOOP_CONTROLS: IMeetingPlayerControls = {
  available: false,
  playing: false,
  activeSeekTime: null,
  play: () => undefined,
  pause: () => undefined,
  rewind: () => undefined,
  playWindow: () => undefined,
  resume: () => undefined,
  speed: 1,
  setSpeed: () => undefined,
  subscribeProgress: () => () => undefined,
}

const MeetingPlayerContext = createContext<IMeetingPlayerControls>(NOOP_CONTROLS)

export function MeetingPlayerProvider({
  value,
  children,
}: {
  value: IMeetingPlayerControls
  children: React.ReactNode
}): React.ReactElement {
  return <MeetingPlayerContext.Provider value={value}>{children}</MeetingPlayerContext.Provider>
}

/** Read the shared meeting-player transport. Defaults to inert controls when no provider is mounted. */
export function useMeetingPlayer(): IMeetingPlayerControls {
  return useContext(MeetingPlayerContext)
}

/**
 * Owns the imperative player ref + observable playback state, and derives the
 * `IMeetingPlayerControls` value the provider broadcasts. The host wires the
 * returned `playerRef` and `onPlayingChange` into its `<Player>` and wraps its
 * subtree in `<MeetingPlayerProvider value={controls}>`.
 */
export function useMeetingPlayerController(available: boolean): {
  controls: IMeetingPlayerControls
  playerRef: React.RefObject<IAudioPlayerRef | null>
  onPlayingChange: (playing: boolean) => void
  onProgress: (currentTime: number, duration: number) => void
  onSpeedChange: (speed: number) => void
} {
  const playerRef = useRef<IAudioPlayerRef>(null)
  const progressListeners = useRef(new Set<(currentTime: number, duration: number) => void>())
  const [playing, setPlaying] = useState(false)
  const [activeSeekTime, setActiveSeekTime] = useState<number | null>(null)
  const [speed, setSpeedState] = useState(1)

  const play = useCallback(
    (seconds: number): void => {
      const p = playerRef.current
      if (!p) return
      // Already cued to this fragment → resume in place; otherwise jump to it.
      if (activeSeekTime === seconds) {
        p.play()
      } else {
        p.seekTo(seconds)
        setActiveSeekTime(seconds)
      }
    },
    [activeSeekTime],
  )

  const pause = useCallback((): void => {
    playerRef.current?.pause()
  }, [])

  const rewind = useCallback((seconds: number): void => {
    playerRef.current?.seekTo(seconds)
    setActiveSeekTime(seconds)
  }, [])

  const playWindow = useCallback((start: number, end: number): void => {
    const p = playerRef.current
    if (!p) return
    p.playWindow(start, end)
    setActiveSeekTime(start)
  }, [])

  const resume = useCallback((): void => {
    playerRef.current?.play()
  }, [])

  const setSpeed = useCallback((rate: number): void => {
    setSpeedState(rate)
    playerRef.current?.setPlaybackRate(rate)
  }, [])

  const subscribeProgress = useCallback((listener: (currentTime: number, duration: number) => void): (() => void) => {
    progressListeners.current.add(listener)
    return () => {
      progressListeners.current.delete(listener)
    }
  }, [])

  const onProgress = useCallback((currentTime: number, duration: number): void => {
    for (const listener of progressListeners.current) listener(currentTime, duration)
  }, [])

  const onSpeedChange = useCallback((next: number): void => {
    setSpeedState(next)
  }, [])

  const controls = useMemo<IMeetingPlayerControls>(
    () => ({
      available,
      playing,
      activeSeekTime,
      play,
      pause,
      rewind,
      playWindow,
      resume,
      speed,
      setSpeed,
      subscribeProgress,
    }),
    [available, playing, activeSeekTime, play, pause, rewind, playWindow, resume, speed, setSpeed, subscribeProgress],
  )

  return { controls, playerRef, onPlayingChange: setPlaying, onProgress, onSpeedChange }
}
